import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AnalyticsFilters,
  CampaignStat,
  GenderData,
  GroupData,
  LineData,
  OverviewData,
  ProgramData,
  TimelinePoint,
} from '@core/interfaces/analytics.interface';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import { CustomizerSettingsService } from '@core/services/customizer-settings.service';
import { LinesGroupsService } from '@core/services/linesGroups/lines-groups.service';
import { MessageService } from 'primeng/api';
import { Subject, forkJoin, takeUntil } from 'rxjs';

@Component({
  selector: 'app-analytics-dashboard',
  templateUrl: './analytics-dashboard.component.html',
  styleUrls: ['./analytics-dashboard.component.scss'],
})
export class AnalyticsDashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Filter dropdown data
  lines: any[] = [];
  groups: any[] = [];
  private allGroups: any[] = [];

  // Selected filters
  selectedLine: string | null = null;
  selectedGroup: string | null = null;
  selectedGranularity: 'day' | 'week' | 'month' = 'month';
  dateFrom: Date | null = null;
  dateTo: Date | null = null;

  // Dashboard data
  overview: OverviewData | null = null;
  genderData: GenderData[] = [];
  lineData: LineData[] = [];
  groupData: GroupData[] = [];
  participantTimeline: TimelinePoint[] = [];
  conversationTimeline: TimelinePoint[] = [];
  programData: ProgramData[] = [];
  campaignData: CampaignStat[] = [];

  loading = false;

  granularityOptions = [
    { label: 'Por día', value: 'day' },
    { label: 'Por semana', value: 'week' },
    { label: 'Por mes', value: 'month' },
  ];

  constructor(
    private analyticsService: AnalyticsService,
    private linesGroupsService: LinesGroupsService,
    public themeService: CustomizerSettingsService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.loadFilterData();
    this.loadAll();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadFilterData(): void {
    this.linesGroupsService.getListLines().subscribe((res: any) => {
      if (res.ok) {
        this.lines = res.results || [];
        this.allGroups = this.lines.flatMap((l: any) =>
          (l.groups || []).map((g: any) => ({
            id: g.id || g._id,
            name: g.name,
            lineName: l.name,
          })),
        );
        this.groups = this.allGroups;
      }
    });
  }

  onLineChange(): void {
    if (this.selectedLine) {
      const line = this.lines.find((l: any) => l.id === this.selectedLine);
      this.groups = (line?.groups || []).map((g: any) => ({
        id: g.id || g._id,
        name: g.name,
        lineName: line.name,
      }));
    } else {
      this.groups = this.allGroups;
    }
    this.selectedGroup = null;
    this.loadAll();
  }

  applyFilters(): void {
    this.loadAll();
  }

  clearFilters(): void {
    this.selectedLine = null;
    this.selectedGroup = null;
    this.selectedGranularity = 'month';
    this.dateFrom = null;
    this.dateTo = null;
    this.groups = this.allGroups;
    this.loadAll();
  }

  onDateRangeChange(): void {
    if (this.dateFrom && this.dateTo) {
      this.applyFilters();
    }
  }

  private getFilters(): AnalyticsFilters {
    const f: AnalyticsFilters = {};
    if (this.selectedLine) f.line = this.selectedLine;
    if (this.selectedGroup) f.group = this.selectedGroup;
    if (this.selectedGranularity) f.granularity = this.selectedGranularity;
    if (this.dateFrom) f.from = this.dateFrom.toISOString();
    if (this.dateTo) f.to = this.dateTo.toISOString();
    return f;
  }

  loadAll(): void {
    this.loading = true;
    const f = this.getFilters();

    forkJoin({
      overview: this.analyticsService.getOverview(f),
      gender: this.analyticsService.getParticipantsByGender(f),
      byLine: this.analyticsService.getParticipantsByLine(f),
      byGroup: this.analyticsService.getParticipantsByGroup(f),
      pTimeline: this.analyticsService.getRegistrationTimeline(f),
      cTimeline: this.analyticsService.getConversationTimeline(f),
      byProgram: this.analyticsService.getConversationsByProgram(f),
      campaigns: this.analyticsService.getCampaignStats(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (r) => {
          this.overview = r.overview.data;
          this.genderData = r.gender.data || [];
          this.lineData = r.byLine.data || [];
          this.groupData = r.byGroup.data || [];
          this.participantTimeline = r.pTimeline.data || [];
          this.conversationTimeline = r.cTimeline.data || [];
          this.programData = r.byProgram.data || [];
          this.campaignData = r.campaigns.data || [];
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar los datos del dashboard',
            life: 5000,
          });
        },
      });
  }

  exportCSV(): void {
    if (!this.campaignData?.length) return;
    const headers = ['Código', 'Estado', 'Grupo', 'Registros'];
    const rows = this.campaignData.map((c) => [
      c.code,
      c.state === 'A' ? 'Activa' : 'Inactiva',
      c.group,
      c.participantCount,
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `analytics_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }
}
